import { Fragment, useEffect, useState } from "react";
import {
    XMarkIcon
} from "@heroicons/react/24/outline";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";

import { classNames, prettyDateTime } from "../lib/utils";
import { BackendObj } from "../lib/backend";
import { IOrganization, ITransactionLogbookEvent } from "../lib/backend/auth.types.generated";

import { LoadingSpinnerLimit } from "./LoadingSpinner";


type TransactionLogbookProps = {
    org: IOrganization;
    open: boolean;
    setOpen: (open: boolean) => void;
}

export function TransactionLogbook(props: TransactionLogbookProps) {
    const { org, open, setOpen } = props;

    const [logbook_list, setLogbookList] = useState<ITransactionLogbookEvent[] | undefined>(undefined);

    useEffect(() => {
        if (open) {
            BackendObj.auth.getAdminOrgTransactionLogbook({ org_uuid: org.uuid })
                .then(({ events }) => { setLogbookList(events); });
        }
    }, [org, open]);

    return <Fragment>
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={setOpen}>
                <div className="fixed inset-0" />
                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                            <TransitionChild
                                as={Fragment}
                                enter="transform transition ease-in-out duration-500 sm:duration-700"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-500 sm:duration-700"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <DialogPanel className="pointer-events-auto w-screen max-w-full lg:ml-64 xl:ml-72">
                                    <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                        <div className="px-4 sm:px-6">
                                        <div className="flex flex-row items-center justify-between">
                                                <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                                    Transaction Logbook for {org.name}
                                                </DialogTitle>
                                                <div className="flex-grow" />
                                                <div className="ml-3 flex h-7 items-center">
                                                    <button
                                                        type="button"
                                                        className="relative rounded-md bg-white text-gray-400 hover:text-gray-500"
                                                        onClick={() => setOpen(false)}
                                                    >
                                                        <span className="absolute -inset-2.5" />
                                                        <span className="sr-only">Close panel</span>
                                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="relative mt-6 flex-1 px-4 sm:px-6">

                                            <table className="min-w-full divide-y divide-gray-300">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" />
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Balance</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Transaction ID</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Details</th>
                                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Queue</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                    {logbook_list && logbook_list.map((event, idx) => (
                                                        <Fragment key={idx}>
                                                            <tr className={classNames("text-sm text-gray-500")}>
                                                                <td className="whitespace-nowrap py-4 pl-2 font-semibold sm:pl-3 align-top">{idx + 1}</td>
                                                                <td className="whitespace-nowrap px-3 py-4 align-top">{prettyDateTime(event.timestamp)}</td>
                                                                <td className="whitespace-nowrap px-3 py-4 align-top">{event.balance}</td>
                                                                <td className="px-3 py-4 align-top">{event.event_type}</td>
                                                                <td className="px-3 py-4 align-top">{event.amount}</td>
                                                                <td className="px-3 py-4 align-top">{event.transaction_id}</td>
                                                                <td className="px-3 py-4 align-top">{event.details}</td>
                                                                <td className="px-3 py-4 align-top">{event.credit_queue.map((q, idx) => `#${idx + 1} Amount: ${q.amount} | TXID: ${q.transaction_id} | Expiration: ${q.expiration_ts ? prettyDateTime(q.expiration_ts) : ""}`).join("\n")}</td>
                                                            </tr>

                                                        </Fragment>
                                                    ))}
                                                    {!logbook_list && <tr>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" colSpan={8}>
                                                            <LoadingSpinnerLimit />
                                                        </td>
                                                    </tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition>
    </Fragment>;
}